import { cloneDeep, omit } from 'lodash'

import { Feature, Triggers, WorkgridDatasource } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { AppThunk } from '@/store'
import { createAction, request } from '@/store/actions'
import { gzip } from '@/utilities/compression'

import * as types from './actionTypes'
import { removeStepCount, transformVariableData } from './utils'

const APP_BUILDER_V1 = 'appbuilder'
const APP_BUILDER_V2 = 'appbuilderV2'
const APP_BUILDER_V3 = 'appbuilderV3'

export const fetchAppInstance = (instanceId: string): AppThunk => {
  return dispatch => {
    return dispatch(
      request(
        `/spaces/:spaceId/instances/${instanceId}`,
        types.FETCH_APP_BUILDER_APP_INSTANCE,
        {},
        { service: APP_BUILDER_V1 }
      )
    )
  }
}

export const setAppInstance = (template: any): AppThunk => {
  return dispatch => {
    return new Promise(resolve => {
      resolve(dispatch(createAction(types.CREATE_APP_BUILDER_APP_INSTANCE, { response: template })))
    })
  }
}

export const setFeature = (feature: any): AppThunk => {
  return dispatch => {
    return new Promise(resolve => {
      resolve(dispatch(createAction(types.CREATE_APP_BUILDER_FEATURE, { response: feature })))
    })
  }
}

export const fetchDataSourceDocs = (dataSourceKey: string | undefined, isPaginatedEnabled?: boolean): AppThunk => {
  return dispatch => {
    return dispatch(
      request(
        `/spaces/:spaceId/datasources/${dataSourceKey}`,
        types.FETCH_APP_BUILDER_DATASOURCE_DOCS,
        {
          headers: { 'content-type': 'application/json' }
        },
        { service: APP_BUILDER_V3 },
        { dataSourceKey, isPaginatedEnabled }
      )
    )
  }
}

interface FeatureProps {
  instanceId: string
  featureId: string
  showLoader?: boolean
}

export const fetchAppInstanceFeature = ({ instanceId, featureId, showLoader = true }: FeatureProps): AppThunk => {
  return dispatch => {
    return dispatch(
      request(
        `/spaces/:spaceId/instances/${instanceId}/features/${featureId}`,
        showLoader ? types.FETCH_APP_BUILDER_FEATURE : types.FETCH_APP_BUILDER_FEATURE_NO_LOADER,
        {},
        { service: APP_BUILDER_V1 }
      )
    )
  }
}

export interface UpdateFeatureProps {
  instanceId: string
  feature: Feature
  payload?: {}
  shouldSave?: boolean
}

export interface ExecuteAppInstanceFeatureProps {
  instanceId: string
  feature: Feature
  payload?: {}
  isV2Response?: boolean
  dataSources?: Partial<WorkgridDatasource>[]
}

export const updateAppInstanceFeature = ({ feature, instanceId, shouldSave = true }: UpdateFeatureProps): AppThunk => {
  const featureClone = cloneDeep(feature)
  if (featureClone.workflow) {
    featureClone.workflow.steps = removeStepCount(transformVariableData(featureClone.workflow.steps, false))
  }

  return dispatch => {
    const updatedWorkflow = dispatch(updateFeatureWorkflow({ id: feature.id, workflow: featureClone.workflow }))

    return shouldSave
      ? dispatch(
          request(
            `/spaces/:spaceId/instances/${instanceId}/features/${feature.id}`,
            types.UPDATE_APP_BUILDER_FEATURE,
            {
              method: 'PUT',
              body: gzip(omit(featureClone, 'id', 'lastFailedExecutionTime')),
              headers: {
                'x-workgrid-accept-encoding': 'workgrid-gzip',
                'x-workgrid-content-encoding': 'workgrid-gzip',
                'Content-Type': 'application/json'
              }
            },
            { service: APP_BUILDER_V1 }
          )
        )
      : updatedWorkflow
  }
}
export interface UpdateFeatureTriggerProps {
  instanceId: string
  featureId: string
  trigger: Triggers
}
export const updateAppInstanceFeatureTrigger = ({
  featureId,
  trigger,
  instanceId
}: UpdateFeatureTriggerProps): AppThunk => {
  return dispatch => {
    return dispatch(
      request(
        `/spaces/:spaceId/instances/${instanceId}/features/${featureId}/trigger`,
        types.UPDATE_APP_BUILDER_FEATURE_TRIGGER,
        { method: 'PUT', body: trigger },
        { service: APP_BUILDER_V1 }
      )
    )
  }
}

export const executeAppInstanceFeature = ({
  feature,
  instanceId,
  payload,
  isV2Response = false,
  dataSources
}: ExecuteAppInstanceFeatureProps): AppThunk => {
  return dispatch => {
    const headers = isV2Response ? { Accept: 'application/vnd.com.workgrid.workflow.response;version=2' } : undefined
    return dispatch(
      request(
        `/spaces/:spaceId/instances/${instanceId}/features/${feature.id}/execute`,
        types.EXECUTE_APP_BUILDER_FEATURE,
        {
          method: 'POST',
          body: dataSources ? { payload, dataSources } : { payload },
          headers
        },
        { service: APP_BUILDER_V2 }
      )
    )
  }
}
interface FetchExecutionProps {
  instanceId: string
  feature: Feature
  executionId: string
  includeStepValues: boolean
  isRecentRun?: boolean
  updateStepOutputModels?: boolean
}

export const fetchAppInstanceFeatureExecution = ({
  feature,
  instanceId,
  executionId,
  includeStepValues,
  updateStepOutputModels = false
}: FetchExecutionProps): AppThunk => {
  return dispatch => {
    const initialPath = `/spaces/:spaceId/instances/${instanceId}/features/${feature.id}/execute/${executionId}`
    const dateString = new Date().toISOString()
    const executionDate = dateString.slice(0, Math.max(0, dateString.indexOf('T')))

    return dispatch(
      request(
        `${initialPath}/${executionDate}?includeStepValues=${includeStepValues}&updateStepOutputModels=${updateStepOutputModels}`,
        types.FETCH_APP_BUILDER_FEATURE_EXECUTION,
        { headers: { Accept: 'application/vnd.com.workgrid.workflow.response;version=2' } },
        { service: APP_BUILDER_V2 }
      )
    )
  }
}

export interface CreateFeatureProps {
  feature: Feature
  instanceId: string
}

export const createAppInstanceFeature = ({ feature, instanceId }: CreateFeatureProps): AppThunk => {
  return dispatch => {
    return dispatch(
      request(
        `/spaces/:spaceId/instances/${instanceId}/features`,
        types.CREATE_APP_BUILDER_FEATURE,
        {
          method: 'POST',
          body: gzip(feature),
          headers: {
            'x-workgrid-accept-encoding': 'workgrid-gzip',
            'x-workgrid-content-encoding': 'workgrid-gzip',
            'Content-Type': 'application/json'
          }
        },
        { service: APP_BUILDER_V1 }
      )
    )
  }
}
export interface PatchFeatureProps {
  feature: Feature
  featureId: string
  instanceId: string
}
export const patchAppInstanceFeature = ({ feature, instanceId, featureId }: PatchFeatureProps): AppThunk => {
  return dispatch => {
    return dispatch(
      request(
        `/spaces/:spaceId/instances/${instanceId}/features/${featureId}`,
        types.PATCH_APP_BUILDER_FEATURE,
        { method: 'PATCH', body: feature },
        { service: APP_BUILDER_V1 }
      )
    )
  }
}
export const resetAppInstance = (): AppThunk => {
  return dispatch => {
    dispatch(createAction(types.RESET_APP_BUILDER_APP_INSTANCE))
  }
}
export const resetFeatureExecution = (): AppThunk => {
  return dispatch => {
    dispatch(createAction(types.RESET_EXECUTE_APP_BUILDER_FEATURE))
  }
}

const updateFeatureWorkflow = ({ id, workflow }: Partial<Feature>): AppThunk => {
  return dispatch => {
    return dispatch(createAction(types.UPDATE_FEATURE_WORKFLOW, { workflow, id }))
  }
}

export const fetchNotificationSources = (): AppThunk => {
  return dispatch => {
    return dispatch(
      request(
        '',
        types.FETCH_APP_BUILDER_NOTIFICATION_SOURCES,
        {
          method: 'GET',
          headers: { 'x-workgrid-space': ':spaceId' }
        },
        {
          args: { spaceId: ':spaceId' },
          service: 'notificationSources'
        }
      )
    )
  }
}
